import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { isIconAccessoryProp } from '#pplx/frontend/aether/components/Accessory/isIconAccessoryProp.js';
import { getButtonClasses } from '#pplx/frontend/aether/components/Button/classes.js';
import { Icon } from '#pplx/frontend/aether/components/Icon/index.js';
import { useMeasureableHandle } from '#pplx/frontend/aether/internal/useMeasureableHandle.js';
import { useRadixProps } from '#pplx/frontend/aether/internal/useRadixProps.js';
import { ButtonPrimitive } from '#pplx/frontend/aether/primitives/ButtonPrimitive/index.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import classNames from 'classnames';
import { useMemo } from 'react';
const ButtonSizeToChevronIconSizeMap = {
    tiny: 'tiny',
    small: 'tiny',
    default: 'tiny',
    large: 'small',
};
const ButtonSizeToTextSizeMap = {
    tiny: 'text-xs',
    small: 'text-sm',
    default: 'text-sm',
    large: 'text-base',
};
const getSelectButtonClasses = (props) => {
    const baseClasses = getButtonClasses(props);
    return classNames(baseClasses, 'justify-between');
};
const getSelectButtonTrailingIconClasses = () => {
    return `ml-1 shrink-0 text-secondary`;
};
/**
 * A tonal button with a chevron indicator used to open a dropdown or listbox. Always renders
 * full-width with a tonal variant.
 *
 * @category components
 * @keywords select, button, components, tonal, with, chevron, indicator, used, open, dropdown,
 *   listbox, always, renders, full-width, variant, size, aria-label, disabled, menu, popover, icon
 * @whenToUse Use SelectButton as the trigger element for selection-based UI patterns such as
 *   dropdown menus, select lists, or custom pickers. It displays the currently selected value with
 *   a trailing chevron icon indicating that it opens additional options. The component inherits
 *   Button sizing and tonal styling, making it visually consistent with other form controls in the
 *   design system.
 * @guidelineDo
 *   - Use SelectButton as the trigger for dropdown menus, select lists, or popover-based pickers
 *   - Pass the currently selected value as children to display it as the button label
 *   - Use the leadingAccessory prop to display an icon before the label for visual context
 *   - Provide an aria-label when the button text alone (e.g., a bare value like '3') does not
 *     convey the purpose
 * @guidelineDont
 *   - Don't use SelectButton as a standalone action button — it is designed as a trigger for
 *     selection UI patterns
 *   - Don't override the trailing chevron icon — it is a built-in affordance that signals
 *     expandability
 *   - Don't use SelectButton without pairing it with a dropdown, popover, or menu component
 *   - Don't remove the fullWidth styling — SelectButton uses justify-between layout that expects
 *     full-width rendering
 * @accessibility
 *   - Renders with aria-haspopup='listbox' by default, signaling to screen readers that activating
 *     the button opens a list of options
 *   - Supports aria-expanded and aria-controls attributes that are typically managed by the parent
 *     Select or Popover component
 *   - Built on ButtonPrimitive which provides the interactable focus ring and reset styles for
 *     keyboard accessibility
 *   - The chevron-down icon provides a visual affordance for sighted users while the ARIA
 *     attributes communicate the same information to assistive technology
 *   - Text truncation is applied to the label with a truncate class — ensure long values are also
 *     available via a title attribute or tooltip
 * @relatedComponents
 *   - button
 *   - menu
 *   - popover
 *   - icon
 */
export function SelectButton({ ref, children, 'aria-expanded': ariaExpanded, 'aria-haspopup': ariaHaspopup = 'listbox', 'aria-controls': ariaControls, 'aria-label': ariaLabel, size = 'default', disabled = false, onClick, leadingAccessory, 
// We intentionally capture the rest of props that could be cloned/passed in by 3rd-party libraries
// such as Radix UI for their own internal usage such as data-state, onFocus, onBlur, etc.
...props }) {
    const targetRef = useMeasureableHandle(ref);
    // Manually pick the props that Radix UI passes in for their own internal usage
    // and pass them down to the ButtonPrimitive.
    const __radixProps = useRadixProps(props);
    const chevronSize = ButtonSizeToChevronIconSizeMap[size];
    const textSize = ButtonSizeToTextSizeMap[size];
    const labelClassName = useMemo(() => classNames(
    // Form controls typically use normal font weight
    'font-normal', 
    // Map text size based on button size
    textSize, 
    // Trims both the start/end of the text
    'text-box-trim-both', 
    // Automatically truncate overflowing text
    'truncate', {
        'pl-1': leadingAccessory,
        'pr-1': true,
        // Allow shrinking below content size
        'min-w-0': true,
    }), [leadingAccessory, textSize]);
    return (_jsxs(ButtonPrimitive, { ref: targetRef, ...__radixProps, disabled: disabled, className: getSelectButtonClasses({
            variant: 'tonal',
            size,
            disabled,
            fullWidth: true,
        }), onClick: onClick, "aria-expanded": ariaExpanded, "aria-haspopup": ariaHaspopup, "aria-controls": ariaControls, "aria-label": ariaLabel, children: [_jsxs("div", { className: "flex min-w-0 flex-1 items-center", children: [leadingAccessory && (_jsx("div", { className: "mr-1 shrink-0", children: isIconAccessoryProp(leadingAccessory) ? (_jsx(Icon, { icon: leadingAccessory, size: size })) : (leadingAccessory) })), _jsx("span", { className: labelClassName, children: children })] }), _jsx("div", { className: getSelectButtonTrailingIconClasses(), children: _jsx(Icon, { icon: $icon('chevron-down'), size: chevronSize }) })] }));
}
//# sourceMappingURL=index.js.map